/**
 * @class KonvaGraph
 */

import * as React from "react";
import { IGraph } from "./interfaces/Graph";
import { IComponent, IConnection } from "./interfaces/Component";
import Component from "./Component";
import { Stage, Layer, FastLayer } from "react-konva";
import { KonvaEventObject, Stage as KonvaStage } from "konva";
import Connection from "./Connection";
import { IPort } from "./interfaces/ComponentDefinition";

import { componentsByType } from "./definitions";

export type Props = {
  graph: IGraph;
  onComponentMoved(component: IComponent, x: number, y: number): void;
  onComponentMoveFinished(component: IComponent): void;
  onConnectionCreated(connection: IConnection): void;
  onConnectionRemoved(connection: IConnection): void;
  onConnectionClicked(component: IComponent, connection: IConnection): void;
  onComponentSelected(component: IComponent | null): void;
  onComponentDeleted(component: IComponent): void;
};

type State = {
  addingConnection: boolean;
  mouseX: number;
  mouseY: number;
  selectedComponentId: string | null;
  componentMousedOver: IComponent | null;
};

export default class KonvaGraph extends React.Component<Props, State> {
  connectionFromComponent: IComponent;
  connectionFromPort: IPort;
  needRAF: boolean = false;
  state = {
    addingConnection: false,
    mouseX: -1,
    mouseY: -1,
    selectedComponentId: null,
    componentMousedOver: null,
  };
  stage: any;
  connections: any

  componentDidMount() {
    window.addEventListener("keydown", this.keyDown);
    this.showConnections();
  }

  componentWillUnmount() {
    window.removeEventListener("keydown", this.keyDown);
  }

  componentWillReceiveProps(newProps: any) {
    if (this.props.graph.id === newProps.graph.id) return;
    this.hideConnections();
  }

  componentDidUpdate() {
    this.showConnections();
  }
  
  showConnections() {
    setTimeout(() => {
      this.connections.show();
      this.stage.batchDraw();
    }, 50);
  }

  hideConnections() {
    this.connections.hide();
    this.stage.batchDraw();
  }

  keyDown = (ev: KeyboardEvent) => {
    if (ev.key === "Esc" || ev.key === "Escape" || ev.keyCode === 27) {
      if (this.state.addingConnection) {
        this.setState({
          addingConnection: false
        });
      } else {
        if (this.state.selectedComponentId) {
          this.props.onComponentSelected(null);
          this.setState({
            selectedComponentId: null
          });
        }
      }
    }
  };

  // handle zoom with wheel
  onWheel = (ev: KonvaEventObject<WheelEvent>) => {
    ev.evt.preventDefault();
    const scaleBy = 1.05;
    const stage = this.stage as KonvaStage;
    const oldScale = this.stage.scaleX();

    const mousePointTo = {
      x: stage.getPointerPosition().x / oldScale - stage.x() / oldScale,
      y: stage.getPointerPosition().y / oldScale - stage.y() / oldScale
    };

    const newScale =
      ev.evt.deltaY > 0 ? oldScale * scaleBy : oldScale / scaleBy;
    stage.scale({ x: newScale, y: newScale });

    const newPos = {
      x: -(mousePointTo.x - stage.getPointerPosition().x / newScale) * newScale,
      y: -(mousePointTo.y - stage.getPointerPosition().y / newScale) * newScale
    };
    stage.position(newPos);
    stage.batchDraw();
  };

  getScaledPointerPosition = () => {
    const pointerPosition = (this.stage as KonvaStage).getPointerPosition();
    const stageX = (this.stage as KonvaStage).getAttr("x");
    const stageY = (this.stage as KonvaStage).getAttr("y");
    const stageScaleX = (this.stage as KonvaStage).getAttr("scaleX");
    const stageScaleY = (this.stage as KonvaStage).getAttr("scaleY");

    const x = (pointerPosition.x - stageX) / stageScaleX;
    const y = (pointerPosition.y - stageY) / stageScaleY;
    return { x, y };
  };

  onMouseMove = (ev: KonvaEventObject<MouseEvent>) => {
    if (!this.stage || this.needRAF) {
      return;
    }
    this.needRAF = true;
    const position = this.getScaledPointerPosition();
    requestAnimationFrame(() => {
      this.setState(
        {
          // decrease 10 for the arrow to be a little behind the pointer
          mouseX: position.x - 10,
          mouseY: position.y
        },
        () => {
          this.needRAF = false;
        }
      );
    });
  };

  onMouseDown = (ev: KonvaEventObject<MouseEvent>) => {
    // add connection if drop target is a Rect
    let toComponent, toComponentId: string | any;
    if (ev.target.getType() === "Shape") {
      toComponentId = ev.target.name();
      toComponent = this.props.graph.components.find((c: IComponent) => c.id === toComponentId)
    }
    if (toComponent && toComponentId) {
      if (this.state.addingConnection) {
        this.setState({
          addingConnection: false
        });
        // create connection
        this.props.onConnectionCreated({
          fromPortId: this.connectionFromPort.id,
          toComponentId: toComponentId,
          fromComponentId: this.connectionFromComponent.id
        });
        return;
      }
      this.setState({
        selectedComponentId: toComponentId
      });
      this.props.onComponentSelected(toComponent);
      return;
    } else {
      this.setState({
        addingConnection: false,
        mouseX: -1,
        mouseY: -1,
      });
    }
  };

  onConnectionStart = (component: IComponent, port: IPort) => {
    this.connectionFromComponent = component;
    this.connectionFromPort = port;
    this.setState({
      addingConnection: true,
      selectedComponentId: component.id
    });
  };

  onConnectionClicked = (component: IComponent, connection: IConnection) => {
    this.props.onConnectionClicked(component, connection);
  };

  onComponentDeleted = (component: IComponent) => {
    if (!component) {
      return;
    }
    this.props.onComponentDeleted(component);
  };

  onComponentMousedOver = (component: IComponent, x: number, y: number) => {
    this.setState({ componentMousedOver: component });
  }

  onComponentMousedOut = (component: IComponent) => {
    this.setState({ componentMousedOver: null });
  }

  /*
   * The module renders components of a graph with their connections
   * 
   * */
  render() {
    const { graph, onComponentMoved, onComponentMoveFinished, onComponentSelected } = this.props;
    const { height = 800, width = 800 } = graph;
    const newConnectionInProgress =
      this.state.addingConnection &&
      this.connectionFromComponent &&
      this.connectionFromPort;

    // All connections that are rendered
    const allConnections = graph.components.reduce(
      (a: Array<IConnection>, component: IComponent) => {
        return a.concat(component.connections || []);
      },
      []
    )

    return (
      <Stage
        onWheel={this.onWheel}
        ref={stage => (this.stage = stage)}
        onMouseDown={this.onMouseDown}
        height={height}
        width={width}
        draggable
        {...(this.state.addingConnection
          ? { onMouseMove: this.onMouseMove }
          : {})}
      >
        <Layer ref={layer => (this.connections = layer)} visible={false}>
          {graph.components.map((component: IComponent) => {
            // Group connections using their toComponentId
            // Connections sharing a toComponentId are rendered as one
            const connectionGroups =
              this.computeConnectionGroups(component.connections);
            
            return connectionGroups.map(
              (group: any) => group.connections.map((connection: IConnection, index: number) => {
                const connectionsSharingPath =
                  this.computeConnectionsSharingPath(connection, allConnections);
                return (
                  <Connection
                    newConnection={false}
                    key={`${component.id}-${connection.toComponentId}-${
                      connection.fromPortId
                    }-${index}`}
                    index={index}
                    fromComponent={component}
                    toComponent={graph.components.find((c: IComponent) => c.id === connection.toComponentId)}
                    connection={connection}
                    connectionsSharingDirection={group.connections}
                    connectionsSharingPath={connectionsSharingPath}
                    onClick={this.onConnectionClicked}
                  />
                );
              })
            ) 
          }
        )}
        </Layer>
        <FastLayer>
          {newConnectionInProgress && (
            <Connection
              newConnection
              fromComponent={this.connectionFromComponent}
              fromPort={this.connectionFromPort}
              mousePosition={{ x: this.state.mouseX, y: this.state.mouseY }}
              componentMousedOver={this.state.componentMousedOver}
            />
          )}
        </FastLayer>
        <Layer>
          {graph.components.map(component => (
            <Component
              key={component.id}
              component={component}
              newConnectionInProgress={Boolean(newConnectionInProgress)}
              selectedComponentId={this.state.selectedComponentId}
              onComponentMoved={onComponentMoved}
              onComponentMoveFinished={onComponentMoveFinished}
              onComponentDeleted={this.onComponentDeleted}
              onConnectionStart={this.onConnectionStart}
              onComponentMousedOver={this.onComponentMousedOver}
              onComponentMousedOut={this.onComponentMousedOut}
              onComponentSelected={onComponentSelected}
            />
          ))}
        </Layer>
      </Stage>
    );
  }

  /*
  * Returns, from an array of connections,
  * those that share a path (or end components) with a given connection
  *
  */
  computeConnectionsSharingPath(
    connection: IConnection,
    allConnections: Array<IConnection>
  ) {
    return allConnections.filter(c => (
      (
        c.fromComponentId === connection.fromComponentId &&
        c.toComponentId === connection.toComponentId
      ) ||
      (
        c.fromComponentId === connection.toComponentId &&
        c.toComponentId === connection.fromComponentId
      )
    )).map(connection => {
      const component: any = this.props.graph.components.find(
        c => c.id === connection.fromComponentId
      );
      const definition = componentsByType[component.graphComponentId];
      const port = definition.ports.find((port: any) => port.id === connection.fromPortId);;
      return { ...connection, port }
    })
  }

  /*
  * Returns connections grouped using their toComponentId
  *
  */
  computeConnectionGroups(connections: Array<IConnection>) {
    const connectionGroups: Array<any> = [];
    connections.forEach(connection => {
      const connectionsSharingDirection = connections.filter(
        c => c.toComponentId === connection.toComponentId
      );
      if (connectionGroups.find(
        (group: any) => group.toComponentId === connection.toComponentId
      )) {}
      else connectionGroups.push({
        toComponentId: connection.toComponentId,
        connections: connectionsSharingDirection
      });
    });

    return connectionGroups;
  }

}
